import { paidPhotoLines } from "./free-enlargement";
/**
 * What counts as a real photo line in the cart.
 *
 * An `orderPhotos` group is created BEFORE its photos are attached (the upload
 * flow calls add-order-photos, then PUTs the files, then registers the
 * metadata). Every step after the first can fail, so a group holding zero
 * photos is a routine residue -- not a thing the customer bought. Rendering it
 * as a line item shows them "10x15 (0 photos)" for an upload they never
 * completed, or, when every group is empty, a cart that claims to hold items
 * while reading as empty. Neither is something they can act on.
 *
 * This is deliberately display-only and defensive: it hides nothing that has
 * photos attached, so it can never conceal a customer's work. It is NOT a
 * substitute for fixing whatever left the group empty.
 */

/** Minimal shape shared by every generated order-photo DTO we read here. */
export interface PhotoGroupLike {
  photos?: { count?: number | null }[] | null;
}

/**
 * Number of prints this group represents, counting per-photo duplicates.
 *
 * Tolerates a missing `photos` relation. The generated DTO types it as
 * required, but several endpoints that write the shared order cache do not load
 * it, so at runtime it is genuinely optional -- dereferencing it unguarded is
 * what turns one bad payload into a blank cart drawer.
 */
export function photoGroupCount(group: PhotoGroupLike | null | undefined): number {
  if (!group || !Array.isArray(group.photos)) return 0;
  return group.photos.reduce((total, photo) => total + (photo?.count || 1), 0);
}

/** True when this group actually has photos attached to it. */
export function hasAttachedPhotos(
  group: PhotoGroupLike | null | undefined,
): boolean {
  return photoGroupCount(group) > 0;
}

/**
 * The groups that are worth showing as cart lines.
 *
 * Note this can only ever REMOVE zero-count groups, so totals computed from the
 * result are unchanged -- an empty group contributes 0 to every sum already.
 */
export function visiblePhotoGroups<T extends PhotoGroupLike>(
  groups: readonly T[] | null | undefined,
): T[] {
  if (!Array.isArray(groups)) return [];
  return groups.filter(hasAttachedPhotos);
}

/**
 * The customer's own photo lines: the visible groups without the free
 * enlargement (see src/utils/free-enlargement.ts). Use it wherever lines are
 * counted, priced, offered upsells or edited; the gift is shown on its own.
 */
export function paidPhotoGroups<T extends PhotoGroupLike>(
  groups: readonly T[] | null | undefined,
): T[] {
  return paidPhotoLines(visiblePhotoGroups(groups));
}
